<template>
  <div class="report-frame-list" :aria-label="frameLabel">
    <section
      ref="measureFrame"
      class="report-frame report-frame--measure"
      aria-hidden="true"
    >
      <div ref="measureFlow" class="frame-viewport frame-flow">
        <div ref="measureContent" class="frame-content">
          <slot />
        </div>
      </div>
    </section>

    <section
      v-if="useContinuousFlow"
      class="report-frame report-frame--continuous"
      :style="backgroundStyle"
      :aria-label="frameLabel"
    >
      <div
        class="frame-viewport frame-viewport--continuous"
        @click="forwardContentAction"
      >
        <div class="frame-content" v-html="flowHtml"></div>
      </div>
    </section>

    <template v-else>
      <section
        v-for="pageIndex in pageCount"
        :key="pageIndex"
        class="report-frame"
        :style="backgroundStyle"
        :aria-label="pageLabel(pageIndex)"
      >
        <img class="frame-background" :src="innerPageImage" alt="" aria-hidden="true" />

        <div class="frame-viewport" @click="forwardContentAction">
          <div class="frame-flow" :style="pageFlowStyle(pageIndex - 1)">
            <div class="frame-content" v-html="flowHtml"></div>
          </div>
        </div>

        <div class="frame-footer">
          <span>{{ reportDate }}</span>
          <span>{{ displayPageNumber(pageIndex - 1) }}</span>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { REPORT_BRAND_NAME } from '../helpers';
import { REPORT_ASSETS } from '../reportAssets';

export default {
  name: 'ReportFrame',
  props: {
    brandName: {
      type: String,
      default: REPORT_BRAND_NAME,
    },
    reportDate: {
      type: String,
      default: '',
    },
    pageNumber: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      flowHtml: '',
      pageCount: 1,
      useContinuousFlow: false,
      innerPageImage: REPORT_ASSETS.innerPage,
      paginationFrame: null,
      resizeObserver: null,
    };
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: ['url("', REPORT_ASSETS.innerPage, '")'].join(''),
      };
    },
    frameLabel() {
      return this.brandName ? [this.brandName, '报告内页'].join('') : '报告内页';
    },
  },
  mounted() {
    this.observeContent();
    this.schedulePagination();
    window.addEventListener('resize', this.schedulePagination);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.schedulePagination);
    }
  },
  updated() {
    this.schedulePagination();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.schedulePagination);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.paginationFrame) cancelAnimationFrame(this.paginationFrame);
  },
  methods: {
    observeContent() {
      const content = this.$refs.measureContent;
      if (!content) return;

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.schedulePagination);
        this.resizeObserver.observe(content);
      }

      Array.from(content.querySelectorAll('img')).forEach((image) => {
        if (!image.complete) image.addEventListener('load', this.schedulePagination, { once: true });
      });
    },
    schedulePagination() {
      if (this.paginationFrame) cancelAnimationFrame(this.paginationFrame);
      this.paginationFrame = requestAnimationFrame(() => {
        this.paginationFrame = requestAnimationFrame(this.paginateContent);
      });
    },
    paginateContent() {
      this.paginationFrame = null;
      const flow = this.$refs.measureFlow;
      const content = this.$refs.measureContent;
      if (!flow || !content || !flow.clientWidth) return;

      this.updateTextBlockPagination(flow, content);

      // Serialize Vue-rendered slot markup so every fixed-height page can display one column slice.
      const nextHtml = content.innerHTML;
      const nextPageCount = this.measureColumnPageCount(flow, content);
      // Some iOS WKWebView versions report only one column for an overflowing multicolumn box.
      // In that case, keep the full report readable in normal document flow instead of clipping it.
      const shouldUseContinuousFlow = nextPageCount === 1
        && this.measureNaturalContentHeight(flow, content) > flow.clientHeight + 1;

      if (this.flowHtml !== nextHtml) this.flowHtml = nextHtml;
      if (this.useContinuousFlow !== shouldUseContinuousFlow) {
        this.useContinuousFlow = shouldUseContinuousFlow;
      }
      if (this.pageCount !== nextPageCount) this.pageCount = nextPageCount;
      this.$emit('page-count-change', shouldUseContinuousFlow ? 1 : nextPageCount);
    },
    updateTextBlockPagination(flow, content) {
      const textBlocks = Array.from(content.querySelectorAll('[data-report-text-block]'));
      textBlocks.forEach((element) => {
        element.classList.remove('report-text-block--keep-together');
      });
      content.getBoundingClientRect();

      textBlocks.forEach((element) => {
        const fragments = Array.from(element.getClientRects())
          .filter((rect) => rect.width > 0 && rect.height > 0);
        const naturalHeight = this.measureTextBlockHeight(flow, element);
        const lineElement = element.querySelector('p') || element;
        const lineHeight = Number.parseFloat(window.getComputedStyle(lineElement).lineHeight) || 16;
        const minimumFragmentHeight = lineHeight * 3;
        const hasShortFragment = fragments.length > 1
          && fragments.some((rect) => rect.height + 1 < minimumFragmentHeight);

        element.classList.toggle(
          'report-text-block--keep-together',
          hasShortFragment && naturalHeight <= flow.clientHeight + 1,
        );
      });
    },
    measureTextBlockHeight(flow, element) {
      const naturalBlock = element.cloneNode(true);
      naturalBlock.classList.remove('report-text-block--keep-together');
      naturalBlock.style.position = 'absolute';
      naturalBlock.style.top = '0';
      naturalBlock.style.left = '0';
      naturalBlock.style.display = 'block';
      naturalBlock.style.width = String(flow.clientWidth).concat('px');
      naturalBlock.style.height = 'auto';
      naturalBlock.style.minHeight = '0';
      naturalBlock.style.visibility = 'hidden';
      naturalBlock.style.pointerEvents = 'none';
      flow.appendChild(naturalBlock);

      try {
        const style = window.getComputedStyle(naturalBlock);
        const marginTop = Number.parseFloat(style.marginTop) || 0;
        const marginBottom = Number.parseFloat(style.marginBottom) || 0;
        return Math.max(
          naturalBlock.scrollHeight,
          naturalBlock.getBoundingClientRect().height,
        ) + marginTop + marginBottom;
      } finally {
        flow.removeChild(naturalBlock);
      }
    },
    measureColumnPageCount(flow, content) {
      const flowRect = flow.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const measuredWidth = Math.max(
        flow.scrollWidth,
        content.scrollWidth,
        contentRect.width,
        contentRect.right - flowRect.left,
      );

      // CSS columns can exceed an exact page-width multiple by a few subpixels.
      // Round to the nearest column so that rounding noise does not create a blank trailing page.
      return Math.max(1, Math.round(measuredWidth / flow.clientWidth));
    },
    measureNaturalContentHeight(flow, content) {
      const naturalContent = content.cloneNode(true);
      naturalContent.classList.add('frame-content--natural-measure');
      flow.appendChild(naturalContent);

      try {
        return Math.max(
          naturalContent.scrollHeight,
          naturalContent.getBoundingClientRect().height,
        );
      } finally {
        flow.removeChild(naturalContent);
      }
    },
    forwardContentAction(event) {
      let actionElement = event.target;
      while (
        actionElement
        && actionElement !== event.currentTarget
        && !actionElement.hasAttribute('data-report-action')
      ) {
        actionElement = actionElement.parentElement;
      }
      if (!actionElement || actionElement === event.currentTarget) return;

      const action = actionElement.getAttribute('data-report-action');
      if (!action) return;

      event.preventDefault();
      this.$emit('content-action', action);
    },
    pageFlowStyle(pageIndex) {
      return {
        transform: ['translateX(-', pageIndex * 100, '%)'].join(''),
      };
    },
    displayPageNumber(pageIndex) {
      const firstPageNumber = Number(this.pageNumber);
      if (Number.isFinite(firstPageNumber)) return firstPageNumber + pageIndex;
      return pageIndex === 0 ? this.pageNumber : '';
    },
    pageLabel(pageIndex) {
      return [this.frameLabel, '第', pageIndex, '页'].join('');
    },
  },
};
</script>

<style lang="scss" scoped>
.report-frame-list {
  position: relative;
  width: 100%;
  background: #fff;
}

.report-frame-list > .report-frame + .report-frame {
  margin-top: 4px;
}

.report-frame {
  position: relative;
  width: 100%;
  height: 141.428571vw;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
  padding: 11.5% 3.2% 7%;
  background-color: #f8f3e8;
  background-position: top center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  border: 0;
  color: #68311d;
  font-family: "Source Han Sans SC", "Source Han Sans CN", "Noto Sans CJK SC",
    "PingFang SC", "Microsoft YaHei", sans-serif;
  isolation: isolate;
}

.report-frame--measure {
  position: absolute;
  top: 0;
  left: -10000%;
  z-index: 0;
  margin: 0 !important;
  overflow: visible;
  opacity: 0;
  pointer-events: none;
}

.report-frame--measure .frame-viewport {
  overflow: visible;
}

.frame-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.frame-viewport {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.frame-flow {
  width: 100%;
  height: 100%;
  -webkit-column-count: 1;
  column-count: 1;
  -webkit-column-fill: auto;
  column-fill: auto;
  -webkit-column-gap: 0;
  column-gap: 0;
}

.frame-content {
  position: relative;
  min-height: 100%;
  box-sizing: border-box;
}

.frame-content--natural-measure {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  min-height: 0;
  -webkit-column-count: initial;
  column-count: initial;
  visibility: hidden;
  pointer-events: none;
}

.report-frame--continuous {
  height: auto;
  min-height: 141.428571vw;
  overflow: visible;
  background-repeat: repeat-y;
  background-size: 100% auto;
}

.frame-viewport--continuous {
  height: auto;
  min-height: 100%;
  overflow: visible;
}

.frame-footer {
  position: absolute;
  left: 3.4%;
  right: 3.4%;
  bottom: 0.8%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #68311d;
  font-size: 11.5px;
  line-height: 1;
  font-weight: 600;
}

@media print {
  .report-frame-list {
    width: 210mm;
  }

  .report-frame-list > .report-frame + .report-frame {
    margin-top: 0;
  }

  .report-frame {
    width: 210mm;
    height: 297mm;
    min-height: 0;
    overflow: hidden;
    background-size: 210mm 297mm;
  }

  .report-frame--measure {
    display: none;
  }

  .report-frame.report-frame--continuous {
    height: auto !important;
    min-height: 297mm !important;
    overflow: visible !important;
    background-repeat: repeat-y;
  }

  .frame-viewport--continuous {
    height: auto;
    overflow: visible;
  }

  .frame-background {
    width: 210mm;
    height: 297mm;
  }
}
</style>
